import React, { Fragment, FunctionComponent, useState, useMemo, useEffect } from 'react';
import { Grid, makeStyles, List, ListItem, Typography, Breadcrumbs, Link as UILink } from '@rbx/ui';
import SideBarContent from '@modules/ui/shared/components/SideBarContent';
import { TopNavigationSidebarHeader, useNavigationConfigs } from '@rbx/creator-hub-navigation';
import { useRouter } from 'next/router';
import { useTranslation } from '@rbx/intl';
import styles from '@modules/ui/shared/utils/styles';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import { TMenu } from '@modules/utilities/pageBuild/menu';

const useStyles = makeStyles()((theme) => ({
  listItem: {
    padding: theme.spacing(1.5, 1),
    cursor: 'pointer',
  },

  cursorPointer: {
    cursor: 'pointer',
  },

  sidebarContent: {
    ...styles.wordBreakWrap,
    ...styles.hideScrollbar,
    overflowY: 'scroll',
    fontSize: 14,
    paddingBottom: theme.spacing(4),
  },

  underline: {
    textDecoration: 'underline',
  },
}));

enum MobileSidebarState {
  Menu,
  SubMenu,
  Page,
}

const MobileSidebar: FunctionComponent<React.PropsWithChildren> = () => {
  const {
    classes: { listItem, sidebarContent, underline, cursorPointer },
  } = useStyles();
  const { productNavigationDrawerOpen } = useNavigationConfigs();
  const { getBreadcrumbsItems } = useSideBarContent();
  const { translate } = useTranslation();
  const breadcrumbsItems = useMemo(() => {
    return getBreadcrumbsItems(translate);
  }, [getBreadcrumbsItems, translate]);
  const [state, setState] = useState(MobileSidebarState.Menu);
  const [selectedMenu, setSelectedMenu] = useState<TMenu | null>(null);
  const router = useRouter();
  const { menuData, menu, menuItem } = useAppConfig();
  useEffect(() => {
    setState(
      breadcrumbsItems && breadcrumbsItems.length > 0
        ? MobileSidebarState.Page
        : MobileSidebarState.Menu,
    );
    setSelectedMenu(null);
  }, [productNavigationDrawerOpen, breadcrumbsItems]);

  if (state === MobileSidebarState.Page && menu && menuItem) {
    return (
      <Fragment>
        <TopNavigationSidebarHeader>
          <Breadcrumbs aria-label={translate('Label.Breadcrumbs')} separator='>'>
            <UILink
              className={cursorPointer}
              underline='none'
              color='inherit'
              onClick={() => {
                setSelectedMenu(null);
                setState(MobileSidebarState.Menu);
              }}>
              <Typography className={underline} color='primary'>
                {translate('Heading.Learn')}
              </Typography>
            </UILink>
            <UILink
              className={cursorPointer}
              underline='none'
              color='inherit'
              onClick={() => {
                setSelectedMenu(menu);
                setState(MobileSidebarState.SubMenu);
              }}>
              <Typography className={underline} color='primary'>
                {menu.title}
              </Typography>
            </UILink>
            <Typography color='primary'>{menuItem.title}</Typography>
          </Breadcrumbs>
        </TopNavigationSidebarHeader>
        <Grid className={sidebarContent} container>
          <SideBarContent />
        </Grid>
      </Fragment>
    );
  }

  if (state === MobileSidebarState.SubMenu && selectedMenu) {
    return (
      <Fragment>
        <TopNavigationSidebarHeader>
          <Breadcrumbs aria-label={translate('Label.Breadcrumbs')} separator='>'>
            <UILink
              className={cursorPointer}
              underline='none'
              color='inherit'
              onClick={() => {
                setSelectedMenu(null);
                setState(MobileSidebarState.Menu);
              }}>
              <Typography className={underline} color='primary'>
                {translate('Heading.Learn')}
              </Typography>
            </UILink>
            <Typography color='primary'>{selectedMenu.title}</Typography>
          </Breadcrumbs>
        </TopNavigationSidebarHeader>
        <List>
          {selectedMenu.items.map(({ title, path }) => (
            <ListItem
              key={title}
              onClick={() => {
                router.push(path);
              }}
              className={listItem}>
              <Typography color='primary' variant='largeLabel1'>
                {title}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <TopNavigationSidebarHeader>
        <Typography variant='largeLabel1'>{translate('Heading.Learn')}</Typography>
      </TopNavigationSidebarHeader>
      <List>
        {menuData.map(({ title, path, items }) => (
          <ListItem
            key={title}
            onClick={() => {
              if (items.length > 0) {
                setSelectedMenu(menuData.find((m) => m.title === title) as TMenu);
                setState(MobileSidebarState.SubMenu);
              } else if (path) {
                router.push(path);
              }
            }}
            className={listItem}>
            <Typography color='primary' variant='largeLabel1'>
              {title}
            </Typography>
          </ListItem>
        ))}
      </List>
    </Fragment>
  );
};

export default MobileSidebar;
